import React, { useRef, useState, useCallback } from 'react';
import { Box } from '@mui/material';

// ─── Cursor-tracking orb glow wrapper ─────────────────────────
// Renders a soft radial gradient that follows the mouse across a card,
// simulating light shining through frosted glass.
const CursorOrbCard = React.memo(({ children, color, isLight, borderRadius = 3 }) => {
  const cardRef = useRef(null);
  const orbRef = useRef(null);
  const [active, setActive] = useState(false);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current || !orbRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    orbRef.current.style.transform = `translate(${x}px, ${y}px)`;
  }, []);

  const handleMouseEnter = useCallback(() => setActive(true), []);
  const handleMouseLeave = useCallback(() => setActive(false), []);

  return (
    <Box
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        height: '100%',
        borderRadius,
      }}
    >
      {/* Orb container — clips the orb to card shape without clipping card lift */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          borderRadius,
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <Box
          ref={orbRef}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: 280,
            height: 280,
            borderRadius: '50%',
            marginLeft: '-140px',
            marginTop: '-140px',
            background: isLight
              ? `radial-gradient(circle, ${color}18 0%, ${color}08 40%, transparent 70%)`
              : `radial-gradient(circle, ${color}22 0%, ${color}0a 40%, transparent 70%)`,
            opacity: active ? 1 : 0,
            transition: 'opacity 0.35s ease',
            willChange: 'transform',
          }}
        />
      </Box>
      {/* Card content — sits above the orb, free to lift on hover */}
      <Box sx={{ position: 'relative', zIndex: 2, height: '100%' }}>
        {children}
      </Box>
    </Box>
  );
});

export default CursorOrbCard;
